import { useState, useEffect, useMemo, useCallback } from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import { logger } from '../../services/logger';
import apiClient from '../../services/api/apiClient';
import axios from 'axios';
import IAlertType from '../../utils/types/props/IAlertType';
import AlertComponent from '../../components/utility/AlertComponent';
import { Spinner } from 'react-bootstrap';
import ClientSelect from '../../components/select/ClientSelect';
import { IClient } from '../../utils/types/IClient';
import CarteSelect from '../../components/select/CarteSelect';
import TypePriseSelect from '../../components/select/TypePriseSelect';
import { useAuth } from '../../utils/context/AuthContext';
import PdvSelect from '../../components/select/PdvSelect';
import AgentSelect from '../../components/select/AgentSelect';
import { IAgent } from '../../utils/types/IAgent';
import { DetailLivraison } from '../../utils/types/DetailLivraison';
import PriseGrid from '../../components/grid/PriseGrid';
import { Carte } from '../../utils/types/Carte';
import { Helmet } from 'react-helmet-async';
import { formatAxiosError } from '../../utils/formatAxiosError';
import { useQuery } from '@tanstack/react-query';
import QueryErrorAlerts, { QueryErrorEntry } from '../../components/others/QueryErrorAlerts';

const PriseCarburant = () => {
    const [isExporting, setIsExporting] = useState<boolean>(false);

    const [selectedCardCode, setSelectedCardCode] = useState<string | null>(null);
    const [selectedTypePrise, setSelectedTypePrise] = useState<string>('');
    const [selectedNumCarte, setSelectedNumCarte] = useState<string | null>(null);
    const [startDate, setStartDate] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);
    const [selectedPdvId, setSelectedPdvId] = useState<number | null>(null);
    const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);
    const [searchTerm, setSearchTerm] = useState<string | null>(null);
    const [onlyWithCard, setOnlyWithCard] = useState<boolean>(false);

    const { user } = useAuth();

    const [systemAlerts, setSystemAlerts] = useState<{ type: IAlertType; message: string }[]>([]);
    const handleDismissAlert = (index: number) => {
        setSystemAlerts((prevAlerts) => prevAlerts.filter((_, i) => i !== index));
    };

    // ---

    const {
        data: clients = [],
        isLoading: isLoadingClients,
        isFetching: isFetchingClients,
        refetch: refetchClients,
        isError: isClientsError,
        error: clientsError,
    } = useQuery<IClient[]>({
        queryKey: ['clients'],
        queryFn: async () => {
            const res = await apiClient.get('/api/clients/getClients');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des clients' },
        staleTime: 1000 * 60 * 10, // 10 min
    });

    const {
        data: serverDate = null,
        refetch: refetchServerDate,
        isError: isServerDateError,
        error: serverDateError,
    } = useQuery<string>({
        queryKey: ['serverDate'],
        queryFn: async () => {
            const res = await apiClient.get(`/api/data/serverDt`);
            return res.data;
        },
        meta: { title: 'Chargement de la date serveur' },
        retry: 3,
        staleTime: 0,
    });

    const {
        data: cartes = [],
        isLoading: isLoadingCartes,
        isFetching: isFetchingCartes,
        refetch: refetchCartes,
        isError: isCartesError,
        error: cartesError,
    } = useQuery<Carte[]>({
        queryKey: ['cartes', selectedCardCode],
        queryFn: async () => {
            const res = await apiClient.get('/api/cartes/byClient', {
                params: {
                    codeClient: selectedCardCode,
                },
            });
            return res.data ?? [];
        },
        meta: { title: 'Chargement des cartes' },
        enabled: !!selectedCardCode,
    });

    const {
        data: pointsDeVente = [],
        isLoading: isLoadingPointsDeVente,
        isFetching: isFetchingPointsDeVente,
        refetch: refetchPointsDeVente,
        isError: isPointsDeVenteError,
        error: pointsDeVenteError,
    } = useQuery({
        queryKey: ['pointsDeVente'],
        queryFn: async () => {
            const response = await apiClient.get('/api/pdv');
            return response.data ?? [];
        },
        enabled: !!user?.adminOrCommercial,
        meta: { title: 'Chargement des points de vente' },
        staleTime: 1000 * 60 * 60, // 1 hour
    });

    const {
        data: agents = [],
        isLoading: isLoadingAgents,
        isFetching: isFetchingAgents,
        refetch: refetchAgents,
        isError: isAgentsError,
        error: agentsError,
    } = useQuery<IAgent[]>({
        queryKey: ['agents', selectedPdvId],
        queryFn: async () => {
            const res = await apiClient.get('/api/agents/byPdv', {
                params: {
                    idPdv: selectedPdvId,
                },
            });
            return res.data ?? [];
        },
        meta: { title: 'Chargement des agents' },
        enabled: !!selectedPdvId,
        staleTime: 1000 * 60 * 60, // 1 hour
    });

    const {
        data: prises = [],
        isLoading: isPrisesLoading,
        isError: isPrisesError,
        error: prisesError,
        refetch: refetchPrises,
    } = useQuery<DetailLivraison[]>({
        queryKey: [
            'prises',
            {
                selectedCardCode: selectedCardCode,
                selectedTypePrise: selectedTypePrise,
                selectedNumeroCarte: selectedNumCarte,
                selectedIdPdv: selectedPdvId,
                selectedIdAgent: selectedAgentId,
                dateDebut: startDate,
                dateFin: endDate,
                searchTerm: searchTerm,
                onlyWithCard: onlyWithCard,
            },
        ],
        queryFn: async () => {
            const response = await apiClient.get('/api/prises/getData', {
                params: {
                    selectedCardCode: selectedCardCode,
                    selectedTypePrise: selectedTypePrise,
                    selectedNumeroCarte: selectedNumCarte,
                    selectedIdPdv: selectedPdvId,
                    selectedIdAgent: selectedAgentId,
                    dateDebut: startDate,
                    dateFin: endDate,
                    searchTerm: searchTerm,
                    onlyWithCard: onlyWithCard,
                },
            });
            return response.data ?? [];
        },
        meta: { title: 'Chargement des prises' },
        enabled: false,
    });

    // ---

    const handleLoadData = useCallback(async () => {
        if (!selectedCardCode) return;
        refetchPrises();
    }, [selectedCardCode, refetchPrises]);

    const exportData2Excel = async () => {
        const source = axios.CancelToken.source();

        try {
            setIsExporting(true);

            const response = await apiClient.get('/api/prises/getData2Excel', {
                params: {
                    selectedCardCode: selectedCardCode,
                    selectedTypePrise: selectedTypePrise,
                    selectedNumeroCarte: selectedNumCarte,
                    selectedIdPdv: selectedPdvId,
                    selectedIdAgent: selectedAgentId,
                    dateDebut: startDate,
                    dateFin: endDate,
                    searchTerm: searchTerm,
                    onlyWithCard: onlyWithCard,
                },
                responseType: 'blob',
                cancelToken: source.token,
            });

            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const downloadUrl = window.URL.createObjectURL(blob);

            const now = new Date();
            const timestamp = now.toISOString().replace(/[:.]/g, '-');
            const fileName = `prise-carburant-${timestamp}.xlsx`;

            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();

            // Nettoyage
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (err: any) {
            if (!axios.isCancel(err)) {
                setSystemAlerts((prevAlerts) => [
                    ...prevAlerts,
                    {
                        type: 'danger',
                        message: "Erreur lors de l'export Excel: " + formatAxiosError(err)?.message,
                    },
                ]);
            }

            logger.error(err);
        } finally {
            setIsExporting(false);
        }

        return () => {
            source.cancel('Annulation de la requête');
        };
    };

    // ---

    useEffect(() => {}, []);

    useEffect(() => {
        if (isLoadingClients || isFetchingClients) return;

        if ((clients ?? []).length === 1) {
            setSelectedCardCode(clients![0].cardCode);
        }
    }, [clients, isLoadingClients, isFetchingClients]);

    useEffect(() => {
        handleLoadData();
    }, [selectedCardCode, handleLoadData]);

    useEffect(() => {
        if (serverDate) {
            const serverDt = new Date(serverDate);

            const localYear = serverDt.getFullYear();
            const localMonth = serverDt.getMonth() + 1;
            const localDate = serverDt.getDate();

            const pad = (n: any) => n.toString().padStart(2, '0');

            const startDateString = `${localYear}-${pad(localMonth)}-01`;
            const endDateString = `${localYear}-${pad(localMonth)}-${pad(localDate)}`;

            setStartDate(startDateString);
            setEndDate(endDateString);
        }
    }, [serverDate]);

    // ---

    const selectedClient = useMemo(() => (clients ?? []).find((client) => client.cardCode === selectedCardCode) ?? null, [clients, selectedCardCode]);

    const queryErrors = useMemo<QueryErrorEntry[]>(
        () => [
            { isError: isClientsError, error: clientsError, title: 'Erreur lors du chargement des clients', refetch: refetchClients },
            { isError: isServerDateError, error: serverDateError, title: 'Erreur lors du chargement de la date serveur', refetch: refetchServerDate },
            { isError: isCartesError, error: cartesError, title: 'Erreur lors du chargement des cartes', refetch: refetchCartes },
            { isError: isPointsDeVenteError, error: pointsDeVenteError, title: 'Erreur lors du chargement des points de vente', refetch: refetchPointsDeVente },
            { isError: isAgentsError, error: agentsError, title: 'Erreur lors du chargement des agents', refetch: refetchAgents },
            { isError: isPrisesError, error: prisesError, title: 'Erreur lors du chargement des prises', refetch: handleLoadData },
        ],
        [isClientsError, clientsError, refetchClients, isServerDateError, serverDateError, refetchServerDate, isCartesError, cartesError, refetchCartes, isPointsDeVenteError, pointsDeVenteError, refetchPointsDeVente, isAgentsError, agentsError, refetchAgents, isPrisesError, prisesError, handleLoadData],
    );

    // ---

    return (
        <>
            <Helmet>
                <title>Prises Carburants :: Portail PETROSIL</title>
            </Helmet>

            <div className="page-header d-print-none">
                <div className="container-xl">
                    <div className="row g-2 align-items-center">
                        <div className="col">
                            <div className="page-pretitle">Listing</div>
                            <h2 className="page-title">Prises Carburants</h2>
                        </div>
                        <div className="col-auto ms-auto d-print-none"></div>
                    </div>
                </div>
            </div>
            <div className="page-body">
                <div className="container-xl">
                    <AlertComponent alerts={systemAlerts} onDismiss={handleDismissAlert} />
                    <QueryErrorAlerts queries={queryErrors} />

                    <div className="card">
                        <div className="accordion accordion-flush" id="accordion-flush">
                            <div className="accordion-item">
                                <button className="accordion-header" type="button" data-bs-toggle="collapse" data-bs-target="#collapse-1-flush" aria-expanded="true">
                                    <div className="accordion-header-text">
                                        <h4>Filtre</h4>
                                    </div>
                                    <div className="accordion-header-toggle">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-1">
                                            <path d="M6 9l6 6l6 -6"></path>
                                        </svg>
                                    </div>
                                </button>
                                <div id="collapse-1-flush" className="accordion-collapse collapse show" data-bs-parent="#accordion-flush">
                                    <div className="accordion-body">
                                        <form
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                handleLoadData();
                                            }}
                                        >
                                            <div className="row gx-3 gy-2 align-items-end">
                                                <div className="col-12 col-sm-6 col-lg-3">
                                                    <label>Client *</label>
                                                    <ClientSelect clients={clients ?? []} isClientsLoading={isLoadingClients || isFetchingClients} selectedCardCode={selectedCardCode} setSelectedCardCode={setSelectedCardCode} />
                                                </div>

                                                <div className="col-12 col-sm-6 col-lg-2">
                                                    <label>Type prise</label>
                                                    <TypePriseSelect setSelectedTypePrise={setSelectedTypePrise} />
                                                </div>

                                                <div className="col-12 col-sm-6 col-lg-3">
                                                    <label>Carte</label>
                                                    <CarteSelect cartes={cartes ?? []} isCartesLoading={isLoadingCartes || isFetchingCartes} setSelectedNumCarte={setSelectedNumCarte} />
                                                </div>

                                                <div className="col-6 col-sm-3 col-lg-2">
                                                    <label>Du</label>
                                                    <input type="date" className="form-control" value={startDate ?? ''} onChange={(e) => setStartDate(e.target.value)} />
                                                </div>

                                                <div className="col-6 col-sm-3 col-lg-2">
                                                    <label>Au</label>
                                                    <input type="date" className="form-control" value={endDate ?? ''} onChange={(e) => setEndDate(e.target.value)} />
                                                </div>

                                                {user?.adminOrCommercial && (
                                                    <>
                                                        <div className="col-12 col-sm-6 col-lg-3">
                                                            <label>Station</label>
                                                            <PdvSelect pointsDeVente={pointsDeVente} isPointsDeVenteLoading={isLoadingPointsDeVente || isFetchingPointsDeVente} setSelectedPdvId={setSelectedPdvId} selectedPdvId={selectedPdvId} />
                                                        </div>

                                                        <div className="col-12 col-sm-6 col-lg-3">
                                                            <label>Agent</label>
                                                            <AgentSelect agents={agents ?? []} isAgentsLoading={isLoadingAgents || isFetchingAgents} setSelectedItemId={setSelectedAgentId} selectedItemId={selectedAgentId} />
                                                        </div>

                                                        <div className="col-12 col-sm-12 col-lg-4">
                                                            <label>Terme de recherche</label>
                                                            <input type="text" className="form-control" value={searchTerm ?? ''} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Bon, chauffeur, plaque, etc." />
                                                        </div>
                                                    </>
                                                )}
                                            </div>

                                            {user?.adminOrCommercial && (
                                                <div className="row mt-2">
                                                    <div className="col-auto">
                                                        <div className="form-check">
                                                            <input id="onlyWithCard" type="checkbox" className="form-check-input" checked={onlyWithCard} onChange={(e) => setOnlyWithCard(e.target.checked)} />
                                                            <label className="form-check-label" htmlFor="onlyWithCard">
                                                                Uniquement avec carte
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="mt-1">
                                                <div className="btn-group">
                                                    <button type="submit" className="btn btn-outline-secondary" disabled={isPrisesLoading}>
                                                        {isPrisesLoading ? (
                                                            <>
                                                                <Spinner as="span" animation="border" size="sm" />
                                                                &nbsp;Générer
                                                            </>
                                                        ) : (
                                                            'Générer'
                                                        )}
                                                    </button>

                                                    <button type="button" className="btn btn-outline-secondary dropdown-toggle dropdown-toggle-split" data-bs-toggle="dropdown">
                                                        <span className="visually-hidden">Toggle Dropdown</span>
                                                    </button>

                                                    <ul className="dropdown-menu">
                                                        <li>
                                                            <button
                                                                className="dropdown-item"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    exportData2Excel();
                                                                }}
                                                                disabled={isPrisesLoading || isExporting}
                                                            >
                                                                Export To Excel
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {isExporting && (
                        <div className="d-flex flex-column justify-content-center align-items-center p-3">
                            <p className="mb-2 text-muted text-center">En cours d'exportation ...</p>
                            <div className="w-50">
                                <div className="progress">
                                    <div className="progress-bar progress-bar-striped progress-bar-animated bg-primary" role="progressbar" style={{ width: '100%' }}></div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mt-2">
                        <PriseGrid prises={prises} client={selectedClient} adminOrCommercial={user?.adminOrCommercial} />
                    </div>
                </div>
            </div>
        </>
    );
};

PriseCarburant.Layout = DefaultLayout;

export default PriseCarburant;
